import { useState } from "react";
import { TEvent, Tournament, TQuiz } from "../../../types/tournamentTypes";
import {
  DateInput,
  NumberInput,
  StringInput,
} from "../../../components/Inputs";
import { Row } from "../../../components/Row";
import { useRequestHandler } from "../../../hooks/requestHandler";
import {
  calculateTeamPlacements,
  createTournamentEvent,
  savePage,
  updateTournamentEvent,
} from "../../../state/actions/tournamentActions";
import { deepCopy } from "../../../util/generalUtil";
import { Check } from "../../../components/Check/Check";

export function EditTournamentEvent(props: {
  isCreate: boolean;
  onGoBack: () => void;
  event: TEvent;
  tournament: Tournament;
}) {
  const handler = useRequestHandler({
    onSuccess: () => {
      props.onGoBack();
    },
  });
  const [thisEvent, setEvent] = useState({ ...props.event });
  const [placeholderSpots, setPlaceholderSpots] = useState(0);
  const getBlackLine = () => {
    let line = "";
    thisEvent.blackLine?.forEach((item, index) => {
      if (index > 0) line += ",";
      line += isNaN(item) ? "" : item;
    });
    return line;
  };
  const [blackLine, setBlackLine] = useState(getBlackLine());
  const getRoundNames = () => {
    let names = "";
    if (thisEvent.roundNames) {
      Object.keys(thisEvent.roundNames).forEach((key, index) => {
        if (index > 0) names += ",";
        names += `${key}:${thisEvent.roundNames![key]}`;
      });
    }
    return names;
  };
  const [roundNames, setRoundNames] = useState(getRoundNames());

  const [prelimTitle, setPrelimTitle] = useState("");
  const [prelimAddTeams, setPrelimAddTeams] = useState(false);

  const getQuizGrid = () => {
    let maxRound = 0;
    let maxSite = 0;
    thisEvent.quizzes.forEach((quiz) => {
      if (quiz.round > maxRound) maxRound = quiz.round;
      if (quiz.site > maxSite) maxSite = quiz.site;
    });

    // all quizzes, [round, site]
    const quizzesByRound: TQuiz[][] = [[]];
    for (let i = 0; i < maxRound; i++) {
      quizzesByRound.push([]);
    }
    thisEvent.quizzes.forEach((quiz) => {
      quizzesByRound[quiz.round].push(quiz);
    });

    return { maxRound, maxSite, quizzesByRound };
  };

  return (
    <div>
      <h3>Edit Event Details</h3>
      {props.isCreate && (
        <StringInput
          caption="Event Name"
          value={thisEvent.eventName}
          onChange={(eventName) => setEvent({ ...thisEvent, eventName })}
        />
      )}
      <DateInput
        value={thisEvent.date}
        onChange={(date) => setEvent({ ...thisEvent, date })}
        caption="Date"
      />
      <NumberInput
        caption="Order"
        value={thisEvent.order}
        onChange={(order) => setEvent({ ...thisEvent, order })}
      />
      <StringInput
        caption="Description"
        value={thisEvent.description}
        onChange={(description) => setEvent({ ...thisEvent, description })}
      />
      <StringInput
        caption="Black Line"
        belowText="Deliminate sites with commas"
        value={blackLine}
        onChange={setBlackLine}
      />
      <button
        className="link"
        onClick={() => {
          let result: number[] = [1];
          const { maxRound, maxSite, quizzesByRound } = getQuizGrid();

          // Find final round
          const lastRound = thisEvent.quizzes.find(
            (q) => q.round === maxRound && q.site === 1
          );
          if (!lastRound) return;
          let validTeams = new Set(lastRound.teams.map((t) => t.teamName));

          // Find lowest site with one of the three teams
          for (let i = maxRound - 1; i >= 1; i--) {
            const allowedTeams = validTeams;
            if (result[0] === maxSite) {
              result = [result[0], ...result];
              continue;
            }
            let highestSite: TQuiz | undefined;
            quizzesByRound[i].forEach((quiz) => {
              if (quiz.teams.some((t) => allowedTeams.has(t.teamName))) {
                if (!highestSite || quiz.site > highestSite.site)
                  highestSite = quiz;
              }
            });

            if (highestSite) {
              quizzesByRound[i].forEach((quiz) => {
                if (quiz.site <= highestSite!.site) {
                  quiz.teams.forEach((team) => {
                    allowedTeams.add(team.teamName);
                  });
                }
              });
              validTeams = allowedTeams;
              result = [Math.max(highestSite.site, result[0]), ...result];
            } else {
              return;
            }
          }

          setBlackLine(result.join(","));
        }}
      >
        Calculate Black Line
      </button>
      <StringInput
        caption="Round Names"
        belowText="Deliminate rounds and names with colons and separate rounds with commas"
        value={roundNames}
        onChange={setRoundNames}
      />
      <Row style={{ marginTop: 20 }}>
        <button className="secondary-btn" onClick={props.onGoBack}>
          Cancel
        </button>
        <button
          className="clickable"
          style={{ marginLeft: 10 }}
          onClick={() => {
            const resultEvent = {
              ...thisEvent,
            };

            // Black Line
            if (!blackLine) {
              delete resultEvent.blackLine;
            } else {
              const sites = blackLine.split(",");
              const newBlackLine = sites.map((item) => Number.parseInt(item));
              resultEvent.blackLine = newBlackLine;
            }

            // Round Names
            if (!roundNames) {
              delete resultEvent.roundNames;
            } else {
              const sites = roundNames.split(",");
              const newNames: Record<string, string> = {};
              sites.forEach((site) => {
                const parts = site.split(":");
                if (parts.length === 2) newNames[parts[0]] = parts[1];
              });
              resultEvent.roundNames = newNames;
            }

            const action = props.isCreate
              ? createTournamentEvent(
                  props.tournament.pageId,
                  props.tournament.secondaryId,
                  resultEvent
                )
              : updateTournamentEvent(
                  props.tournament.pageId,
                  props.tournament.secondaryId,
                  resultEvent
                );
            handler.runRequest(action, "Saving the event...", "Event saved.");
          }}
        >
          Save Event Details
        </button>
      </Row>
      <hr />
      <NumberInput
        caption="Placeholder Spots"
        value={placeholderSpots}
        onChange={setPlaceholderSpots}
      />
      <Row style={{ marginTop: 20 }}>
        <button
          className="secondary-btn"
          onClick={() => {
            handler.runRequest(
              calculateTeamPlacements(
                props.tournament.pageId,
                props.tournament.secondaryId,
                props.event.eventName,
                placeholderSpots
              ),
              "Calculating team placements...",
              "Team placements added."
            );
          }}
        >
          Calculate Team Placements
        </button>
      </Row>
      <hr />
      <StringInput
        caption="Prelim Title"
        value={prelimTitle}
        onChange={setPrelimTitle}
      />
      <Row style={{ marginTop: 20 }}>
        <Check
          checked={prelimAddTeams}
          onClick={setPrelimAddTeams}
          style={{ width: 230 }}
        >
          Add Teams to Prelim
        </Check>
        {props.tournament.preliminaryResults && (
          <button
            className="secondary-btn"
            style={{ marginLeft: 10 }}
            onClick={() => {
              const title =
                prelimTitle ||
                props.tournament.preliminaryResults?.data.find(
                  (col) => col.eventName === props.event.eventName
                )?.title;
              if (!title) return;

              const newPrelim = deepCopy(props.tournament.preliminaryResults!);
              if (prelimTitle) {
                newPrelim.data.push({
                  title: prelimTitle,
                  eventName: props.event.eventName,
                });
              }

              const { maxRound, quizzesByRound } = getQuizGrid();
              const teamPlacements: string[] = [];
              // Top site to bottom site, first team to last team
              quizzesByRound[maxRound]
                .sort((q1, q2) => q1.site - q2.site)
                .forEach((quiz) => {
                  [...quiz.teams]
                    .sort((t1, t2) => t1.p - t2.p)
                    .forEach((team) => {
                      teamPlacements.push(team.teamName);
                    });
                });
              teamPlacements.forEach((team, index) => {
                if (!newPrelim.teams[team] && prelimAddTeams)
                  newPrelim.teams[team] = {
                    data: {},
                    total: 0,
                  };
                if (newPrelim.teams[team]) {
                  const score = teamPlacements.length - index;
                  newPrelim.teams[team].data[title] = score;
                  newPrelim.teams[team].total += score;
                }
              });

              handler.runRequest(
                savePage("UPDATE", {
                  ...props.tournament,
                  preliminaryResults: newPrelim,
                })
              );
            }}
          >
            Calculate Placement Points
          </button>
        )}
      </Row>
    </div>
  );
}
